import { BUTTON_NAMES, type ButtonName } from '@hf/shared';
import { DEVICE_ID, padIndexOf, type Bindings } from './types';

/** P1: A/D/W/S + J action + K special (ARCHITECTURE §8). */
export const KEYBOARD_P1: Bindings = {
  L: ['KeyA'],
  R: ['KeyD'],
  U: ['KeyW'],
  D: ['KeyS'],
  A: ['KeyJ'],
  S: ['KeyK'],
};

/** P2: arrows + Numpad1/Numpad2 or . and / */
export const KEYBOARD_P2: Bindings = {
  L: ['ArrowLeft'],
  R: ['ArrowRight'],
  U: ['ArrowUp'],
  D: ['ArrowDown'],
  A: ['Numpad1', 'Period'],
  S: ['Numpad2', 'Slash'],
};

/** Single-player keyboard: both layouts drive player 1. */
export const KEYBOARD_SOLO: Bindings = {
  L: [...KEYBOARD_P1.L, ...KEYBOARD_P2.L],
  R: [...KEYBOARD_P1.R, ...KEYBOARD_P2.R],
  U: [...KEYBOARD_P1.U, ...KEYBOARD_P2.U, 'Space'],
  D: [...KEYBOARD_P1.D, ...KEYBOARD_P2.D],
  A: [...KEYBOARD_P1.A, ...KEYBOARD_P2.A, 'KeyZ'],
  S: [...KEYBOARD_P1.S, ...KEYBOARD_P2.S, 'KeyX'],
};

/** Gamepad standard mapping (button indices as strings; "axis0-"/"axis0+"/"axis1-"/"axis1+" for the left stick). */
export const GAMEPAD_DEFAULT: Bindings = {
  L: ['14', 'axis0-'],
  R: ['15', 'axis0+'],
  U: ['12', '0', 'axis1-'],
  D: ['13', 'axis1+'],
  A: ['2', '1'],
  S: ['3', '5'],
};

/** Touch has a fixed layout; its "bindings" are the on-screen button ids. */
export const TOUCH_DEFAULT: Bindings = {
  L: ['left'],
  R: ['right'],
  U: ['jump'],
  D: ['down'],
  A: ['action'],
  S: ['special'],
};

/** Deep copy so callers can never mutate the frozen defaults. */
export const cloneBindings = (b: Bindings): Bindings => ({
  L: [...b.L],
  R: [...b.R],
  U: [...b.U],
  D: [...b.D],
  A: [...b.A],
  S: [...b.S],
});

/** Factory defaults for a DEVICE_ID. Unknown ids get the keyboard solo layout. */
export const defaultBindingsFor = (deviceId: string): Bindings => {
  if (deviceId === DEVICE_ID.KB_P1) return cloneBindings(KEYBOARD_P1);
  if (deviceId === DEVICE_ID.KB_P2) return cloneBindings(KEYBOARD_P2);
  if (deviceId === DEVICE_ID.TOUCH) return cloneBindings(TOUCH_DEFAULT);
  if (padIndexOf(deviceId) >= 0) return cloneBindings(GAMEPAD_DEFAULT);
  return cloneBindings(KEYBOARD_SOLO);
};

const MAX_CODES_PER_BUTTON = 6;
const KEY_CODE_RE = /^[A-Za-z][A-Za-z0-9]{0,31}$/;
const PAD_CODE_RE = /^(?:\d{1,2}|axis\d-|axis\d\+)$/;

/** Is `code` a well-formed physical code for that device kind? */
export const isValidCode = (deviceId: string, code: unknown): code is string =>
  typeof code === 'string' && (padIndexOf(deviceId) >= 0 ? PAD_CODE_RE.test(code) : KEY_CODE_RE.test(code));

/**
 * Validate persisted bindings (localStorage is untrusted): unknown buttons are ignored, malformed codes dropped,
 * a code listed on two buttons keeps only its first, and a button left with no valid code falls back to those of its
 * default codes no other button uses (never one key driving two buttons; possibly none if all are taken).
 * Never throws.
 */
export const sanitizeBindings = (deviceId: string, raw: unknown): Bindings => {
  const out = defaultBindingsFor(deviceId);
  if (deviceId === DEVICE_ID.TOUCH || !raw || typeof raw !== 'object') return out;
  const rec = raw as Partial<Record<ButtonName, unknown>>;
  const used = new Set<string>();
  const restore: ButtonName[] = [];
  for (const name of BUTTON_NAMES) {
    const v = rec[name];
    const codes: string[] = [];
    if (Array.isArray(v)) {
      for (const c of v) {
        if (isValidCode(deviceId, c) && !used.has(c)) {
          codes.push(c);
          used.add(c);
        }
        if (codes.length >= MAX_CODES_PER_BUTTON) break;
      }
    }
    if (codes.length > 0) out[name] = codes;
    else restore.push(name);
  }
  for (const name of restore) {
    out[name] = out[name].filter((c) => !used.has(c));
    for (const c of out[name]) used.add(c);
  }
  return out;
};

/**
 * Pad codes that are the default movement inputs (D-pad, left stick): rebinding a pad button never strips them. A
 * captured right-stick / trigger axis is a primary code like any button: the next rebind replaces it.
 */
const isPadDirection = (code: string): boolean => /^(?:1[2-5]|axis[01][-+])$/.test(code);

export interface RebindOutcome {
  bindings: Bindings;
  /** The captured code belonged to another button, which got `code` (the rebound button's old primary) instead. */
  swapped: { button: ButtonName; code: string } | null;
}

/**
 * Rebind for the Settings screen: `code` becomes the primary code of `button`. Swap semantics: if another button
 * used `code`, it gets `button`'s previous primary in the same slot (J was ACTION, JUMP was W → JUMP = J,
 * ACTION = W), so one physical key never drives two buttons and no button is left empty. Keyboard: the button's other
 * codes are dropped (one key per button) unless `keepOthers`. Pads: the button's D-pad / stick directions are kept
 * (rebinding JUMP to X must not stop stick-up from jumping), its other buttons are replaced.
 */
export const rebindDetailed = (
  current: Bindings,
  button: ButtonName,
  code: string,
  keepOthers = false,
  pad = false,
): RebindOutcome => {
  const next = cloneBindings(current);
  const before = current[button];
  const kept = keepOthers
    ? before.filter((c) => c !== code)
    : pad
      ? before.filter((c) => c !== code && isPadDirection(c))
      : [];
  const dropped = before.filter((c) => c !== code && !kept.includes(c));
  const give = dropped[0] ?? null;
  let swapped: RebindOutcome['swapped'] = null;
  for (const name of BUTTON_NAMES) {
    if (name === button) continue;
    const i = next[name].indexOf(code);
    if (i < 0) continue;
    if (give !== null && !swapped && !next[name].includes(give)) {
      next[name][i] = give;
      swapped = { button: name, code: give };
    } else next[name].splice(i, 1);
  }
  next[button] = [code, ...kept].slice(0, MAX_CODES_PER_BUTTON);
  return { bindings: next, swapped };
};

/** `rebindDetailed` without the swap report; pads are detected from the codes (pad codes are numeric / axis). */
export const rebind = (current: Bindings, button: ButtonName, code: string, keepOthers = false): Bindings =>
  rebindDetailed(current, button, code, keepOthers, isPadBindings(current, code)).bindings;

const isPadBindings = (b: Bindings, code: string): boolean =>
  PAD_CODE_RE.test(code) && BUTTON_NAMES.every((n) => b[n].every((c) => PAD_CODE_RE.test(c)));

export const bindingsEqual = (a: Bindings, b: Bindings): boolean =>
  BUTTON_NAMES.every((n) => a[n].length === b[n].length && a[n].every((c, i) => c === b[n][i]));

/** The other half of a shared keyboard (kb:p1 ↔ kb:p2), or null. Both halves play at once in local Versus. */
export const sharedKeyboardPartner = (deviceId: string): string | null =>
  deviceId === DEVICE_ID.KB_P1 ? DEVICE_ID.KB_P2 : deviceId === DEVICE_ID.KB_P2 ? DEVICE_ID.KB_P1 : null;

/**
 * Local Versus on one keyboard: a key may drive only ONE player. `winner` keeps every code; the other half loses
 * the codes the winner uses, and a button left empty gets the winner's freed default for that button (P1 JUMP → ↑
 * makes P2 JUMP W: a swap), else its own default if free. Pure; never mutates its inputs.
 */
export const resolveSharedKeyboard = (
  p1: Bindings,
  p2: Bindings,
  winner: 'kb:p1' | 'kb:p2',
): { p1: Bindings; p2: Bindings } => {
  const win = cloneBindings(winner === DEVICE_ID.KB_P1 ? p1 : p2);
  const lose = cloneBindings(winner === DEVICE_ID.KB_P1 ? p2 : p1);
  const winDefaults = defaultBindingsFor(winner);
  const loseDefaults = defaultBindingsFor(winner === DEVICE_ID.KB_P1 ? DEVICE_ID.KB_P2 : DEVICE_ID.KB_P1);
  const taken = new Set<string>();
  for (const n of BUTTON_NAMES) for (const c of win[n]) taken.add(c);
  for (const n of BUTTON_NAMES) lose[n] = lose[n].filter((c) => !taken.has(c));
  const loseUses = new Set<string>();
  for (const n of BUTTON_NAMES) for (const c of lose[n]) loseUses.add(c);
  for (const n of BUTTON_NAMES) {
    if (lose[n].length > 0) continue;
    const pick = [...winDefaults[n], ...loseDefaults[n]].find((c) => !taken.has(c) && !loseUses.has(c));
    if (pick) {
      lose[n] = [pick];
      loseUses.add(pick);
    }
  }
  return winner === DEVICE_ID.KB_P1 ? { p1: win, p2: lose } : { p1: lose, p2: win };
};
